import { Badge } from '@shared/components/ui/badge';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/components/ui/shadcn/avatar';
import { faBox, faHashtag, faStore } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { OrderPreparation } from '../../models/Order';

interface OrderInfoProps {
  orderRef?: string;
  shopName?: string;
  shopImageHash?: string;
  showPreparation?: boolean;
  preparation?: OrderPreparation;
}

const PREPARATION_STATUS_STYLES: Record<string, string> = {
  '1': 'bg-card-border text-muted-foreground',
  '2': 'bg-bg-warning text-warning',
  '3': 'bg-bg-success text-success',
  '4': 'bg-bg-error text-error',
};

const getPreparationPath = (preparation: OrderPreparation): string => {
  const status = parseInt(preparation.status, 10);
  if (status === 0 || status === 1 || status === 2) {
    return `/order-preparation/picking/${preparation.id}`;
  }
  return `/order-preparation/packing/${preparation.id}`;
};

const InfoRow: FC<{
  icon: ReactNode;
  label: string;
  children: ReactNode;
  testId?: string;
  wrapIcon?: boolean;
}> = ({ icon, label, children, testId, wrapIcon = true }) => (
  <div className="flex items-center gap-4" data-testid={testId}>
    {wrapIcon ? (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-hover-press">{icon}</div>
    ) : (
      <div className="shrink-0">{icon}</div>
    )}
    <div className="min-w-0">
      <p className="text-sm text-complementary-text">{label}</p>
      {children}
    </div>
  </div>
);

export const OrderInfo: FC<OrderInfoProps> = ({
  orderRef,
  shopName,
  shopImageHash,
  showPreparation = false,
  preparation,
}) => {
  const { t } = useTranslation('viewOrdersModule');

  const preparationStatusLabel = useMemo(() => {
    if (!preparation?.status) return null;

    const statusLabels: Record<string, string> = {
      '1': t('sections.preparation.status.pending'),
      '2': t('sections.preparation.status.in-progress'),
      '3': t('sections.preparation.status.completed'),
      '4': t('sections.preparation.status.cancelled'),
    };

    return statusLabels[preparation.status] ?? t('sections.preparation.status.unknown', { status: preparation.status });
  }, [preparation?.status, t]);

  const hasDetails = Boolean(orderRef || shopName || showPreparation);

  if (!hasDetails) return null;

  return (
    <SectionBox className="bg-card-bg" data-testid="order-info">
      <h2 className="text-lg font-bold">{t('sections.order-info.title')}</h2>

      <div className="mt-4 space-y-4">
        {orderRef && (
          <>
            <InfoRow
              icon={<FontAwesomeIcon icon={faHashtag} className="!h-3 !w-3 text-icons" />}
              label={t('sections.order-info.ref')}
              testId="order-info-ref"
            >
              <p className="text-sm font-medium">{orderRef}</p>
            </InfoRow>
            {(shopName || showPreparation) && <div className="border-t border-card-border" />}
          </>
        )}

        {shopName && (
          <>
            <InfoRow
              icon={
                <Avatar className="h-10 w-10">
                  <AvatarImage src={shopImageHash} alt={shopName} />
                  <AvatarFallback className="bg-card-bg">
                    <FontAwesomeIcon icon={faStore} className="!h-3 !w-3 text-success" />
                  </AvatarFallback>
                </Avatar>
              }
              label={t('sections.order-info.shop')}
              testId="order-info-shop"
              wrapIcon={false}
            >
              <p className="font-medium">{shopName}</p>
            </InfoRow>
            {showPreparation && <div className="border-t border-card-border" />}
          </>
        )}

        {showPreparation && (
          <InfoRow
            icon={<FontAwesomeIcon icon={faBox} className="!h-3 !w-3 text-icons" />}
            label={t('sections.preparation.code')}
            testId="order-info-preparation"
          >
            {preparation ? (
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={getPreparationPath(preparation)}
                  className="text-sm font-medium font-mono text-title underline underline-offset-2"
                >
                  {preparation.code}
                </Link>
                {preparationStatusLabel && (
                  <Badge
                    className={`text-xs ${PREPARATION_STATUS_STYLES[preparation.status] ?? 'bg-card-border text-muted-foreground'}`}
                  >
                    {preparationStatusLabel}
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-sm text-subtext">{t('sections.preparation.empty')}</p>
            )}
          </InfoRow>
        )}
      </div>
    </SectionBox>
  );
};
